import { Info } from 'lucide-react'
import type { Kpi } from '@manaro/contracts'
import { Card } from '@/shared/ui/card.tsx'
import { cn } from '@/shared/lib/cn.ts'
import { formatDelta, formatValue } from '@/shared/lib/format.ts'

interface KpiCardProps {
  kpi: Kpi
  currency: string
  variant?: 'default' | 'compact'
}

export function KpiCard({ kpi, currency, variant = 'default' }: KpiCardProps) {
  const isEmpty = kpi.value === null

  return (
    <Card className={cn('flex min-w-0 flex-col gap-2 px-5 py-4.5', variant === 'compact' && 'gap-1.5 py-4')}>
      <div className="flex items-center gap-1.5 text-[13px] text-ink-muted">
        {kpi.label}
        {kpi.hint && (
          <span title={kpi.hint} className="inline-flex cursor-help">
            <Info className="size-3.5 text-ink-faint" />
          </span>
        )}
      </div>

      <div
        className={cn(
          'tabular font-display leading-none font-semibold tracking-[-0.02em]',
          variant === 'compact' ? 'text-[22px]' : 'text-[29px]',
          isEmpty && 'text-ink-faint',
        )}
      >
        {formatValue(kpi.value, kpi.format, currency)}
      </div>

      {(kpi.delta || kpi.note) && (
        <div className="flex flex-wrap items-center gap-1.5">
          {kpi.delta && (
            <span
              className={cn(
                'tabular rounded-md px-2 py-0.5 text-[12px] font-semibold',
                kpi.delta.direction === 'up' ? 'bg-brand-ring text-brand-ink' : 'bg-warn-tint text-warn-ink',
              )}
            >
              {formatDelta(kpi.delta, currency)}
            </span>
          )}
          {kpi.note && <span className="text-[12.5px] text-ink-muted">{kpi.note}</span>}
        </div>
      )}
    </Card>
  )
}
